// client/src/hooks/useRangeSelect.js
// Reusable selection hook with shift-click range selection and drag-select
import { useState, useRef, useCallback, useEffect } from 'react';

const useRangeSelect = (items, idKey = '_id') => {
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [isDragging, setIsDragging] = useState(false);
  const [dragRect, setDragRect] = useState(null);
  const [dragSelectedIds, setDragSelectedIds] = useState(new Set());
  const lastClickedId = useRef(null);
  const dragStart = useRef(null);
  const containerRef = useRef(null);
  const isCoarse = useRef(false);

  useEffect(() => {
    isCoarse.current = window.matchMedia('(pointer: coarse)').matches;
  }, []);

  const getItemIds = useCallback(() => items.map(item => item[idKey]), [items, idKey]);

  const toggleSelect = useCallback((id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    lastClickedId.current = id;
  }, []);

  const handleItemClick = useCallback((id, event) => {
    // Accept native event, React SyntheticEvent, or plain object with shiftKey
    const shiftKey = event?.shiftKey || event?.nativeEvent?.shiftKey || false;
    if (shiftKey && lastClickedId.current != null) {
      const ids = getItemIds();
      const lastIdx = ids.indexOf(lastClickedId.current);
      const currIdx = ids.indexOf(id);
      if (lastIdx !== -1 && currIdx !== -1) {
        const [start, end] = lastIdx < currIdx ? [lastIdx, currIdx] : [currIdx, lastIdx];
        setSelectedIds(prev => {
          const next = new Set(prev);
          for (let i = start; i <= end; i++) next.add(ids[i]);
          return next;
        });
        lastClickedId.current = id;
        return;
      }
    }
    toggleSelect(id);
  }, [getItemIds, toggleSelect]);

  const selectAll = useCallback(() => {
    setSelectedIds(new Set(getItemIds()));
  }, [getItemIds]);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    lastClickedId.current = null;
  }, []);

  // Drag select handlers — use pageX/pageY (document-relative) for start anchor
  const onMouseDown = useCallback((e) => {
    if (isCoarse.current) return;
    const tag = e.target.tagName;
    if (['INPUT', 'BUTTON', 'A', 'SELECT', 'TEXTAREA', 'LABEL', 'SVG', 'PATH'].includes(tag)) return;
    if (e.target.closest('button, a, input, select, textarea, label, [role="button"], svg')) return;
    if (e.button !== 0) return;

    dragStart.current = {
      pageX: e.pageX,
      pageY: e.pageY,
      scrollX: window.scrollX,
      scrollY: window.scrollY,
    };
  }, []);

  const onMouseMove = useCallback((e) => {
    if (!dragStart.current) return;

    const dx = e.pageX - dragStart.current.pageX;
    const dy = e.pageY - dragStart.current.pageY;

    // Require minimum 8px movement to start drag
    if (!isDragging && Math.abs(dx) < 8 && Math.abs(dy) < 8) return;

    if (!isDragging) {
      setIsDragging(true);
      // Prevent text selection during drag
      e.preventDefault();
    }

    // Compute rectangle in viewport (fixed) coordinates
    // Start point in current viewport = original page coords - current scroll
    const startViewX = dragStart.current.pageX - window.scrollX;
    const startViewY = dragStart.current.pageY - window.scrollY;
    const curViewX = e.clientX;
    const curViewY = e.clientY;

    const rect = {
      left: Math.min(startViewX, curViewX),
      top: Math.min(startViewY, curViewY),
      right: Math.max(startViewX, curViewX),
      bottom: Math.max(startViewY, curViewY),
    };
    setDragRect(rect);

    // Auto-scroll when near edges
    const scrollMargin = 40;
    const scrollSpeed = 12;
    if (e.clientY < scrollMargin) {
      window.scrollBy(0, -scrollSpeed);
    } else if (e.clientY > window.innerHeight - scrollMargin) {
      window.scrollBy(0, scrollSpeed);
    }

    // Find intersecting elements using viewport coords (getBoundingClientRect is viewport-relative)
    const container = containerRef.current;
    if (!container) return;
    const elements = container.querySelectorAll('[data-select-id]');
    const preview = new Set();
    elements.forEach(el => {
      const elRect = el.getBoundingClientRect();
      if (elRect.right >= rect.left && elRect.left <= rect.right &&
          elRect.bottom >= rect.top && elRect.top <= rect.bottom) {
        preview.add(el.getAttribute('data-select-id'));
      }
    });
    setDragSelectedIds(preview);
  }, [isDragging]);

  const onMouseUp = useCallback(() => {
    if (isDragging && dragSelectedIds.size > 0) {
      setSelectedIds(prev => {
        const next = new Set(prev);
        dragSelectedIds.forEach(id => next.add(id));
        return next;
      });
    }
    setIsDragging(false);
    setDragRect(null);
    setDragSelectedIds(new Set());
    dragStart.current = null;
  }, [isDragging, dragSelectedIds]);

  // Clean up drag on mouse leave window
  useEffect(() => {
    const handleMouseUp = () => {
      if (dragStart.current) {
        onMouseUp();
      }
    };
    window.addEventListener('mouseup', handleMouseUp);
    return () => window.removeEventListener('mouseup', handleMouseUp);
  }, [onMouseUp]);

  const dragProps = {
    onMouseDown,
    onMouseMove,
    onMouseUp,
    ref: containerRef,
  };

  return {
    selectedIds,
    setSelectedIds,
    handleItemClick,
    toggleSelect,
    selectAll,
    clearSelection,
    dragProps,
    isDragging,
    dragRect,
    dragSelectedIds,
  };
};

export default useRangeSelect;
